import React from 'react';
import { IconButton, IconButtonProps, Tooltip, SxProps, Theme } from '@mui/material';
import { ReactComponent as DefaultCloseIcon } from '../../assets/SVG/close-circle-greay.svg';

// ─────────────────────────────────────────────────────────────────────────────
// Component Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CloseIconButtonProps extends Omit<IconButtonProps, 'children'> {
  /**
   * Accessible label for screen readers (aria-label).
   * @default "Close"
   */
  'aria-label'?: string;
  /**
   * Optional icon override. Defaults to custom close icon.
   */
  icon?: React.ReactNode;
  /**
   * Tooltip for mouse users. If explicitly set to null/empty string, disables the tooltip.
   * @default "Close"
   */
  tooltip?: string | null;
}

/** Default close icon button style */
const defaultCloseIconSx: SxProps<Theme> = {
  p: 0,
  color: '#667085',
  '&:hover': {
    backgroundColor: 'transparent',
  },
};

/**
 * Universal, accessible close button for dialogs, popovers, modals, or custom UI.
 * Supports custom icons, tooltips, aria-labels, and all MUI IconButton props.
 */
const CloseIconButton: React.FC<CloseIconButtonProps> = ({
  'aria-label': ariaLabel = 'Close',
  icon = <DefaultCloseIcon />,
  tooltip = 'Close',
  sx,
  ...rest
}) => {
  const mergedSx = sx ? { ...defaultCloseIconSx, ...sx } : defaultCloseIconSx;
  const isDisabled = Boolean((rest as IconButtonProps).disabled);

  // When tooltip is null or empty, omit altogether
  if (!tooltip) {
    return (
      <IconButton
        size="small"
        aria-label={ariaLabel}
        sx={mergedSx}
        {...rest}
      >
        {icon}
      </IconButton>
    );
  }

  const button = (
    <IconButton
      size="small"
      aria-label={ariaLabel}
      sx={mergedSx}
      {...rest}
    >
      {icon}
    </IconButton>
  );

  return (
    <Tooltip title={tooltip}>
      {/* span workaround for tooltip-on-disabled-button; only needed when disabled */}
      {isDisabled ? <span>{button}</span> : button}
    </Tooltip>
  );
};

export default CloseIconButton;